/* ===================================================================
   ENTRY_MODEL -- the onboarding entry-model A/B flag (Phase 5, spec §7,
   2026-07-07). Steve's LOCKED decision: DO NOT deploy the front-door card
   wall. Let owners in CARD-FREE (reverse trial), get them to the activation
   event, then ask for the card AFTER first value. This flag scaffolds an A/B
   so the wall can be tested later WITHOUT re-plumbing.

   Two arms:
     'reverse_trial'   (DEFAULT) -- no front-door wall. RequireSubscription
                        does NOT hard-lock a no-subscription account to
                        /choose-plan; owners onboard card-free and the card ask
                        is repositioned post-activation (PostActivationCardAsk).
     'front_door_wall' -- the committed-but-undeployed 2026-07-06 gate: a
                        none/expired/canceled account is hard-locked to
                        /choose-plan before it can reach the product. Off by
                        default; reachable ONLY via an explicit override below.

   OVERRIDE PRECEDENCE (first match wins):
     1. localStorage['rhody_entry_model']  -- per-device override for QA and,
        later, client-side A/B bucket assignment (write the assigned arm here).
     2. import.meta.env.VITE_ENTRY_MODEL   -- build/deploy-time override (e.g. a
        wall-arm preview build) without touching code.
     3. ENTRY_MODEL_DEFAULT                 -- 'reverse_trial'.

   TOKENS/RULES: pure config, no UI here.
   =================================================================== */

import { track } from '../lib/analytics';

export type EntryModel = 'reverse_trial' | 'front_door_wall';

/* The locked default. Reverse trial ships; the wall stays off until an A/B
   deliberately turns it on. Do NOT change this to flip the wall on globally --
   assign the arm via the overrides so it stays measurable. */
export const ENTRY_MODEL_DEFAULT: EntryModel = 'reverse_trial';

const VALID_MODELS: readonly EntryModel[] = ['reverse_trial', 'front_door_wall'];
const LS_KEY = 'rhody_entry_model';
/* One-time migration marker (2026-07-09). reverse_trial is the LOCKED live model;
   the front-door wall is retired. Early testers (incl. Steve on
   app.rhodyreviews.com) may still carry a stale
   localStorage['rhody_entry_model']='front_door_wall' from QA -- and localStorage
   OUTRANKS the deploy env, so that stale value would keep hard-locking THEIR
   browser to /choose-plan even after the deployed default flips to reverse_trial.
   We clear that stale wall override exactly ONCE per device, then never touch it
   again (so a DELIBERATE future QA/A-B override via setEntryModelOverride still
   sticks). */
const MIGRATION_KEY = 'rhody_entry_model_wall_cleared_2026_07_09';

function isEntryModel(v: unknown): v is EntryModel {
  return typeof v === 'string' && (VALID_MODELS as readonly string[]).includes(v);
}

/* Idempotent, per-device: clear a STALE front_door_wall override once. Safe to
   call on every resolve -- the marker makes repeat calls no-ops, and a
   deliberately re-set override after migration is left untouched. */
function clearStaleWallOverrideOnce(): void {
  try {
    if (localStorage.getItem(MIGRATION_KEY)) return;
    if (localStorage.getItem(LS_KEY) === 'front_door_wall') {
      localStorage.removeItem(LS_KEY);
    }
    localStorage.setItem(MIGRATION_KEY, '1');
  } catch {
    /* storage unavailable -- nothing to migrate */
  }
}

/* Synchronous resolver (usable outside React too). Reads the override chain
   above and always returns a valid arm. Never throws -- a blocked/absent
   localStorage falls through to env, then the default. */
export function resolveEntryModel(): EntryModel {
  // 0. One-time cleanup of a stale front_door_wall override from early QA, so a
  //    tester's browser is not stuck on the retired wall (localStorage outranks
  //    the deploy env). Idempotent; a deliberate post-migration override sticks.
  clearStaleWallOverrideOnce();
  // 1. Per-device override (QA / A/B bucket assignment).
  try {
    const ls = localStorage.getItem(LS_KEY);
    if (isEntryModel(ls)) return ls;
  } catch {
    /* storage unavailable -- fall through */
  }
  // 2. Build/deploy-time override.
  const env = import.meta.env.VITE_ENTRY_MODEL;
  if (isEntryModel(env)) return env;
  // 3. Locked default.
  return ENTRY_MODEL_DEFAULT;
}

/* Write (or clear) the per-device override. This is the seam a future A/B
   assigner calls once per visitor to lock their bucket, and the QA lever for
   previewing the wall arm locally:
     setEntryModelOverride('front_door_wall')  // force the wall
     setEntryModelOverride(null)               // clear -> back to default */
export function setEntryModelOverride(model: EntryModel | null): void {
  try {
    if (model === null) localStorage.removeItem(LS_KEY);
    else localStorage.setItem(LS_KEY, model);
  } catch {
    /* non-fatal */
  }
}

/* ---- Analytics (spec §7: instrument WHICH arm is active) -------------------
   Delegates to the shared track() emitter (lib/analytics) so experiment
   exposure rides the SAME 'rhody:analytics' bus + dev logging + single real-
   sink seam as every §11 funnel milestone -- activation-rate AND downstream
   paid-retention can be sliced by arm, not just signup count. Payload shape is
   unchanged ({ event:'experiment_exposure', experiment, arm, context, ts }).
   Fire-once is enforced by the caller (useEntryModel), so this is a plain
   emitter. */
export function trackEntryModelExposure(model: EntryModel, context: string): void {
  track('experiment_exposure', {
    experiment: 'onboarding_entry_model',
    arm: model,
    context,
  });
}
